import React, { useMemo, useState } from "react";
import {Button, Dialog, DialogContent, DialogContentText, DialogTitle, Divider, TextField, Typography, DialogActions, CircularProgress, makeStyles} from "@material-ui/core"

import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DoneOutlineIcon from "@material-ui/icons/DoneOutline";
import ErrorIcon from "@material-ui/icons/Error";
import { printAssetCategory } from "../../../../entities/AssetCategory";
import { SaveFileAssetRequest, FileAssetService } from "../../../../services/FIleAssetService";
import { useLibrary, useLibraryAlert } from "../../LibraryPage";
import { AssetCategoryType } from "../../../../entities/AssetType";
import { FieldValidationError, ValidationHelper } from "../../../../services/server/ServerResponses";
import { convertFileAssetToLibraryItem } from "../../../../entities/FileAsset";

const useStyles = makeStyles((theme) => ({
    uploadFileButton: {
        width: '100%',
        height: '300px',
        marginBottom: theme.spacing(2)
    },
    uploadFilePlaceholderText: {
        fontFamily: "Roboto, Helvetica",
        fontSize: "12px",
        marginTop: "12px",
        color: "#5f6368",
        textAlign: 'center'
    },
    uploadFilePlaceholderIcon: {
        width: "90px",
        height: "90px",
        color: "#CCC"
    },
}));

interface UploadFileDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    selectedAssetType: AssetCategoryType;
}

export function UploadFileDialog(props: UploadFileDialogProps) {
    const { open, selectedAssetType} = props;
    const context = useLibrary();
    const libraryAlert = useLibraryAlert();
    const classes = useStyles();

    const [assetName, setAssetName] = useState("");
	const [uploading, setUploading] = useState(false);
    const [fileToUpload, setFileToUpload] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    const [errors, setErrors] = useState<FieldValidationError[]>([]);

    const categoryName = useMemo(() => printAssetCategory(selectedAssetType.category), [selectedAssetType])
    const uploadError = useMemo(() => ValidationHelper.getFieldErrorSummary(errors, "Upload"), [errors]);

    const handleNameChange = (e:React.ChangeEvent<HTMLInputElement>) => {
        setErrors([]);
        setAssetName(e.currentTarget.value)
    }

    const onFileUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        if(file)
            onFileSelected(file);
    }

    const handleFileUploadDrop = (e: React.DragEvent<HTMLElement>) => {
        const file = e.dataTransfer.files[0];

        if(file)
            onFileSelected(file)
    }

    const onFileSelected = async(file: File) => {
        setErrors([]);
        if (file.size >= 26214400)    
            return setErrors([{errors: ["File Too Large"], field: "Upload" }]);

        setFileToUpload(file);
    }

    const onFileUploadSubmit = async (e:React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if(!fileToUpload)
            return setErrors([{errors: ["No file selected"], field: "Upload" }]);

        if(assetName.length === 0)
            return setErrors([{errors: ["Invalid File Name"], field: "Name" }]);

        setUploading(true);
        const uploadPath = await FileAssetService.generateUploadPath(fileToUpload.name);

        if(!uploadPath.success) return libraryAlert("Could not upload File")
        const fileUploadResponse = await FileAssetService.uploadFile(uploadPath.data.url, fileToUpload, setUploadProgress)
        
        if(!fileUploadResponse.success) return libraryAlert("Could not upload File")

        const request: SaveFileAssetRequest = {
            parentId: context.activeFolderId ?? undefined,
            name: assetName,
            category: selectedAssetType.category,
            uploadPath: uploadPath.data.filePath,
            fileSize: fileToUpload.size
        }
        
        let response = await FileAssetService.save(request);
        setUploading(false);

        if(response.validation) return setErrors(response.errors);
        if(response.error) return libraryAlert(response.message);

        setFileToUpload(null);
        context.addLibraryItem(convertFileAssetToLibraryItem(response.data));
        libraryAlert(`${assetName} added to library`);
        return props.onSuccess();
    }

    const onCancel = () => {
        setErrors([]);
        setUploading(false);
        props.onClose();
    }

	return (
		<Dialog open={open} onClose={props.onClose}>
			<DialogTitle id="form-dialog-title">Upload {categoryName}</DialogTitle>
            <form onSubmit={onFileUploadSubmit}>
				<DialogContent>
					<DialogContentText>
						Enter the name of your new {categoryName.toLowerCase()}.
					</DialogContentText>
					<TextField
						required
						autoFocus={true}
						margin="dense"
						id="newItemName"
						label="Name"
						type="text"
						onChange={handleNameChange}
                        value={assetName}
						fullWidth
						error={ValidationHelper.isFieldInError(errors, "Name")}
						helperText={
							<span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, "Name") }} />
						}
					/>
					<Divider />
					<input
						style={{display: "none"}}
						id="flat-button-file"
						onChange={(e) => onFileUploadChange(e)}
						type="file"
						disabled={uploading}
					/>
					<label htmlFor="flat-button-file">
						<Button
							disabled={uploading}
							variant={fileToUpload ? "outlined" : "text"}
							color={fileToUpload ? "primary" : "secondary"}
							onDragOver={(e:any) => e.preventDefault()}
							onDrop={(e:any) => handleFileUploadDrop(e)}
							component="span"
							className={classes.uploadFileButton}
						>
							{fileToUpload && !uploadError && !uploading
                                &&
                                <Typography className={classes.uploadFilePlaceholderText}>
                                    <DoneOutlineIcon className={classes.uploadFilePlaceholderIcon} />
                                    <br />
                                    Document Accepted
                                </Typography>
                            }
                            { !fileToUpload && !uploadError && !uploading &&
                                <Typography className={classes.uploadFilePlaceholderText}>
                                    <CloudUploadIcon className={classes.uploadFilePlaceholderIcon} />
                                    <br />
                                    Upload Here
                                </Typography>
                            }
                            { fileToUpload && !uploadError && uploading &&
                                <Typography className={classes.uploadFilePlaceholderText}>
                                    <CircularProgress className={classes.uploadFilePlaceholderIcon} value={uploadProgress} color="primary" />
                                    <br />
                                    Uploading {uploadProgress}%
                                </Typography>
                            }
                            { uploadError &&
                                <Typography className={classes.uploadFilePlaceholderText}>
                                    <ErrorIcon className={classes.uploadFilePlaceholderIcon} />
                                    <br />
                                    {ValidationHelper.getFieldErrorSummary(errors, "Upload")}
                                </Typography>
                            }
						</Button>
					</label>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={onCancel}
						color="primary"
					>
						Cancel
					</Button>
					{!uploading 
                    ?
						<Button type="submit" color="primary" disabled={!fileToUpload || errors.length !== 0}>
							Upload
						</Button>
					:
						<Button color="primary" disabled>Uploading File</Button>
                    }
				</DialogActions>
            </form>
		</Dialog>
	);
}